import { useState } from 'react'
import { useFeedbackResponses } from '../hooks/useARE'
import { Inbox, Star, CheckCircle, Clock, ChevronDown, ChevronUp } from 'lucide-react'
import { cn } from '@/shared/lib/utils'

const ratingColor = (r: number) => {
  if (r >= 4) return 'text-emerald-600 bg-emerald-50'
  if (r >= 3) return 'text-amber-600 bg-amber-50'
  return 'text-red-600 bg-red-50'
}

export function FeedbackInboxPage() {
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined)
  const { data, isLoading } = useFeedbackResponses({ status: statusFilter })
  const items = data?.data ?? []
  const total = data?.meta?.total ?? 0
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const filters = [
    { value: undefined, label: 'Tous' },
    { value: 'new', label: 'Nouveaux' },
    { value: 'read', label: 'Lus' },
    { value: 'resolved', label: 'Résolus' },
  ]

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif text-2xl font-bold text-ink tracking-tight">Feedback internes</h1>
        <p className="text-ink-muted mt-1">Retours clients soumis via le formulaire confidentiel</p>
      </div>

      <div className="flex items-center gap-2">
        {filters.map((f) => (
          <button key={f.label} onClick={() => setStatusFilter(f.value)}
            className={cn('px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
              statusFilter === f.value ? 'bg-brick text-white' : 'bg-paper-warm text-ink hover:bg-paper-warm')}>
            {f.label}
          </button>
        ))}
        <span className="text-sm text-ink-faint ml-2">{total} retour(s)</span>
      </div>

      <div className="space-y-3">
        {isLoading ? (
          <div className="text-center py-12 text-ink-faint">Chargement…</div>
        ) : !items.length ? (
          <div className="text-center py-12 bg-white rounded-card border border-ink-border">
            <Inbox size={40} className="mx-auto text-ink-faint mb-3" />
            <p className="text-ink-muted">Aucun feedback reçu</p>
            <p className="text-sm text-ink-faint mt-1">Les retours des clients insatisfaits apparaîtront ici.</p>
          </div>
        ) : (
          items.map((fb: any) => {
            const expanded = expandedId === fb.id
            return (
              <div key={fb.id} className="bg-white rounded-card border border-ink-border overflow-hidden">
                <button
                  onClick={() => setExpandedId(expanded ? null : fb.id)}
                  className="w-full flex items-center gap-4 px-5 py-4 hover:bg-paper transition-colors text-left"
                >
                  {fb.rating && (
                    <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm flex-shrink-0', ratingColor(fb.rating))}>
                      {fb.rating}/5
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-ink truncate">
                        {fb.contact_name || fb.contact_email || 'Anonyme'}
                      </p>
                      {fb.category && (
                        <span className="text-[11px] bg-paper-warm text-ink-muted px-2 py-0.5 rounded-full">{fb.category}</span>
                      )}
                    </div>
                    <p className="text-xs text-ink-faint mt-0.5 truncate">
                      {fb.feedback_text?.slice(0, 80) || 'Aucun commentaire'}
                      {fb.feedback_text?.length > 80 ? '…' : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={cn('text-xs font-medium px-2 py-0.5 rounded-full',
                      fb.status === 'new' ? 'bg-red-100 text-red-700' :
                      fb.status === 'resolved' ? 'bg-emerald-100 text-emerald-700' :
                      'bg-paper-warm text-ink'
                    )}>
                      {fb.status === 'new' ? 'Nouveau' : fb.status === 'resolved' ? 'Résolu' : 'Lu'}
                    </span>
                    {fb.submitted_at && (
                      <span className="text-[11px] text-ink-faint">
                        {new Date(fb.submitted_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                      </span>
                    )}
                    {expanded ? <ChevronUp size={14} className="text-ink-faint" /> : <ChevronDown size={14} className="text-ink-faint" />}
                  </div>
                </button>

                {expanded && (
                  <div className="border-t border-ink-border px-5 py-4 bg-paper/50 space-y-3">
                    {fb.feedback_text && (
                      <div>
                        <p className="text-xs font-semibold text-ink-muted uppercase tracking-wide mb-1">Commentaire</p>
                        <p className="text-sm text-ink whitespace-pre-wrap">{fb.feedback_text}</p>
                      </div>
                    )}
                    {fb.rating && (
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map((s) => (
                          <Star key={s} size={16} className={s <= fb.rating ? 'text-amber-400 fill-amber-400' : 'text-ink-border'} />
                        ))}
                      </div>
                    )}
                    {fb.team_note && (
                      <div>
                        <p className="text-xs font-semibold text-ink-muted uppercase tracking-wide mb-1">Note équipe</p>
                        <p className="text-sm text-ink">{fb.team_note}</p>
                      </div>
                    )}
                    {fb.crm_ticket_id && (
                      <p className="text-xs text-ink-faint">Ticket CRM : {fb.crm_ticket_id}</p>
                    )}
                  </div>
                )}
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}
